import { useEffect, useRef, useState } from 'react';

interface PainPointItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  iconBg?: string;
  iconColor?: string;
  color?: string;
}

interface DefaultPainPoint {
  id: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
}

interface PainPointsSectionProps {
  items?: PainPointItem[];
  loading?: boolean;
}

export default function PainPointsSection({ items = [], loading = false }: PainPointsSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fallback hardcoded data nếu không có data từ API
  const defaultPainPoints: DefaultPainPoint[] = [
    {
      id: 'default-1',
      icon: 'ri-time-line',
      iconBg: 'from-red-500/10 to-orange-500/10',
      iconColor: 'text-red-500',
      title: 'Phát hành phần mềm chậm, phụ thuộc thủ công',
      description: 'Mỗi lần release đều cần can thiệp thủ công, kiểm tra lặp đi lặp lại. Thời gian từ code đến production kéo dài hàng tuần.'
    },
    {
      id: 'default-2',
      icon: 'ri-shield-cross-line',
      iconBg: 'from-red-500/10 to-pink-500/10',
      iconColor: 'text-red-600',
      title: 'Bảo mật chỉ được kiểm tra sau khi đã vận hành',
      description: 'Lỗ hổng bảo mật chỉ phát hiện khi hệ thống đã live, chi phí sửa chữa tăng gấp 10 lần so với phát hiện sớm.'
    },
    {
      id: 'default-3',
      icon: 'ri-line-chart-line',
      iconBg: 'from-orange-500/10 to-red-500/10',
      iconColor: 'text-orange-600',
      title: 'Khó kiểm soát chất lượng khi hệ thống mở rộng',
      description: 'Càng nhiều tính năng, càng nhiều team, càng khó đảm bảo chất lượng đồng nhất. Rủi ro tăng theo cấp số nhân.'
    }
  ];

  const painPoints: (PainPointItem | DefaultPainPoint)[] = items.length > 0 ? items : defaultPainPoints;

  return (
    <section ref={sectionRef} className="pt-12 md:pt-16 lg:pt-24 pb-8 md:pb-12 lg:pb-16 bg-[#F8FAFB]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className={`text-center mb-8 md:mb-12 lg:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-block mb-3 md:mb-4">
            <span className="text-xs md:text-sm font-semibold text-[#06b6d4] uppercase tracking-wider">
              THÁCH THỨC HIỆN TẠI
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2a4b7c] leading-tight max-w-[800px] mx-auto px-4">
            Những rào cản khiến doanh nghiệp khó mở rộng hệ thống
          </h2>
        </div>

        {/* Pain Points Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-16 h-16 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Đang tải...</p>
          </div>
        ) : painPoints.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Chưa có nội dung</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {painPoints.map((point, index) => (
            <div 
              key={index}
              className={`group bg-white rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-10 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: isVisible ? `${0.2 + index * 0.1}s` : '0s' }}
            >
              {/* Icon */}
              <div className={`w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-lg md:rounded-xl bg-gradient-to-br ${point.iconBg || 'from-[#06b6d4]/10 to-[#0891b2]/10'} flex items-center justify-center mb-4 md:mb-6`}>
                <i className={`${point.icon} text-2xl md:text-3xl ${'iconColor' in point ? point.iconColor : ('color' in point ? point.color : 'text-[#06b6d4]')}`}></i>
              </div>

              {/* Title */}
              <h3 className="text-lg md:text-xl font-semibold text-[#2a4b7c] mb-3 md:mb-4 leading-snug">
                {point.title}
              </h3>

              {/* Description */}
              <p className="text-sm md:text-base text-[#64748B] leading-relaxed">
                {point.description}
              </p>
            </div>
          ))}
          </div>
        )}
      </div>
    </section>
  );
}
